<template>
  <form
    :id="id"
    :aria-label="
      t('common.additionalParam', {
        param: t('shipments.services', 2),
      })
    "
    class="spot-market-rate-quote-accessorials flex flex-column flex-gap-1 align-items-start"
    tabindex="-1"
  >
    <p v-if="!internalValue.length" class="m-0">
      {{
        t('common.noParam', {
          param: t('common.additionalParam', {
            param: t('shipments.services', 2),
          }),
        })
      }}
    </p>

    <SpotMarketRateQuoteAccessorialEdit
      v-for="(_accessorial, i) of internalValue"
      :id="generateDomId(i)"
      :key="i"
      v-model="internalValue[i]"
      :available-accessorials="availableAccessorials"
      :disabled="disabled"
      class="w-full"
      @remove="removeAccessorial(i)"
    />

    <AddButton
      :aria-controls="id"
      :label="t('common.additionalParam', { param: t('shipments.services') })"
      :disabled="disabled || v$.$invalid"
      @click="addAccessorial"
    />

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      input-id="total-accessorial-charges"
      :label="
        t('common.paramValues', {
          param: t('common.additionalParam', {
            param: t('shipments.services', 2),
          }),
        })
      "
      class="visually-hidden"
    >
      <output
        :id="inputId"
        v-bind="ariaProps"
        name="total-accessorial-charges"
        :for="allAccessorialElementIds"
        :form="id"
      >
        {{ totalAccessorialCharges }}
      </output>
    </InputWrapper>
  </form>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useValidations } from '@rygen/rygen-ui/composables';
import { AccessorialCode } from '@rygen/rygen-ui/model/AccessorialCode';
import { notNull } from '@rygen/rygen-ui/util';

import { RateQuoteAccessorial } from '@/model';

import SpotMarketRateQuoteAccessorialEdit from '@/components/carrierPortal/rateShop/SpotMarketRateQuoteAccessorialEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<RateQuoteAccessorial[]>,
    required: false,
    default: () => [],
  },
  availableAccessorials: {
    type: Array as PropType<AccessorialCode[]>,
    required: false,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, availableAccessorials, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: RateQuoteAccessorial[]): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<RateQuoteAccessorial[]>) => opts,
});
const { v$, validate } = useValidations(internalValue, {});

const allAccessorialElementIds = computed(() =>
  internalValue.value.map((_a, index) => generateDomId(index)).join(' '),
);

const totalAccessorialCharges = computed(() =>
  internalValue.value
    .map((a) => a?.charge)
    .filter(notNull)
    .reduce((total, curr) => total + curr, 0),
);

const generateDomId = (index: number) =>
  `spot-market-rate-result-accessorials-${index}`;

const addAccessorial = async () => {
  if (await validate()) {
    internalValue.value.push(new RateQuoteAccessorial());
  }
};

const removeAccessorial = (i: number) => {
  internalValue.value.splice(i, 1);
};
</script>
